import type { SelectedFilters } from 'src/app/portfolio/state/portfolioGeneralSlice';
import type { GqlRiskBandLevel, GqlSonBandLevel } from 'src/app/portfolio/state/api/riskInsightsEngineSlice';

/**
 * The filters an organisation's estate is read through.
 *
 * One set of filters, read by every view of that estate: the GraphQL site list and
 * portfolio statistics take these fields as query arguments, and the vector tile
 * endpoint takes the same fields as its query string, in snake_case. A filter that
 * narrows one view therefore narrows all of them.
 *
 * CAUTION: every field declared here reaches the tile endpoint, which refuses a
 * query parameter it does not know — one unknown field answers 400 for every tile
 * and the map draws nothing. Add one only when the engine's `SiteFilterQuery`
 * carries it as well as the GraphQL schema.
 */
export type SiteEstateFilters = {
    isic?: string[];
    countries?: string[];
    physicalRiskLevels?: GqlRiskBandLevel[];
    dependencyLevels?: GqlRiskBandLevel[];
    impactLevels?: GqlRiskBandLevel[];
    sonLevels?: GqlSonBandLevel[];
    nameContains?: string;
    /** The instant the estate is read at, as an ISO timestamp. */
    at?: string;
};

/** The name each filter goes by in the URL of the Site Explorer, and the field it
 *  becomes here. Every value the URL carries is checked by `sanitizeSelectedFilters`
 *  before it reaches this.
 *
 *  An allowlist: the URL of this page carries params that are not site filters at
 *  all, and a name no query knows would be refused by the engine. `kba` is absent
 *  because no site query accepts it. */
const FILTER_FIELDS: Record<string, 'isic' | 'countries' | 'physicalRiskLevels' | 'dependencyLevels' | 'impactLevels' | 'sonLevels'> = {
    isic: 'isic',
    country: 'countries',
    physical_risk_levels: 'physicalRiskLevels',
    dependency_levels: 'dependencyLevels',
    impact_levels: 'impactLevels',
    son_levels: 'sonLevels',
};

type SiteEstateFiltersArgs = {
    /** The filters of the URL, already checked against the filter options. */
    selectedFilters: SelectedFilters;
    search?: string;
    at?: string | null;
};

/** What the reader has narrowed the estate to, as every site query reads it. */
export const siteEstateFiltersOf = ({ selectedFilters, search, at }: SiteEstateFiltersArgs): SiteEstateFilters => {
    const filters: SiteEstateFilters = {};

    selectedFilters.forEach(({ name, values }) => {
        const field = FILTER_FIELDS[name];
        if (!field || values.length === 0) return;
        // `sanitizeSelectedFilters` has already checked each value against the levels
        // its dimension allows, which is what makes a band level a band level here.
        Object.assign(filters, { [field]: values });
    });

    if (search) filters.nameContains = search;
    if (at) filters.at = at;

    return filters;
};
